/**
 * Fire Modeling Dashboard
 *
 * Embeds Project Nomad's Dashboard for fire behavior modeling.
 * Uses EM3's adapter to connect Nomad Dashboard to EM3's auth, map, and services.
 *
 * @module components/fireModelingDashboard
 */

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useFusionAuth } from '@fusionauth/react-sdk';
import { OpenNomadProvider, DashboardContainer, AboutModal } from '@nomad/frontend';
import ContextHelp from '../../easymap/ContextHelp/ContextHelp';
// @ts-ignore — Vite resolves this at build time from node_modules
import nomadPkg from '../../../node_modules/@nomad/frontend/package.json';
const nomadVersion = nomadPkg.version;
import mapboxgl from 'mapbox-gl';
import { createEM3NomadAdapter, type FusionAuthUserInfo } from '../../services/nomad';
import { getUserById } from '../../services/fusionAuthService';
import { getNomadConfig } from '../../services/configService';
import { useLayerDrawer } from '../../easymap/layerDrawer';
import { getDrawService } from '../../services/drawing';
import './FireModelingDashboard.scss';

/** Notification state for success/error messages */
interface Notification {
  type: 'success' | 'error';
  message: string;
}

/**
 * Props for FireModelingDashboard
 */
interface FireModelingDashboardProps {
  className?: string;
  onClose?: () => void;
}

/**
 * Color palette for deterministic fire model time-step features.
 * Non-red colors to distinguish from probabilistic outputs.
 */
const DETERMINISTIC_COLORS = [
  '#2196F3', // blue
  '#4CAF50', // green
  '#FF9800', // orange
  '#9C27B0', // purple
  '#00BCD4', // cyan
  '#795548', // brown
  '#607D8B', // blue-grey
  '#E91E63', // pink
  '#3F51B5', // indigo
  '#009688', // teal
];

/**
 * Inject color properties on GeoJSON features that lack them.
 * Assigns colors from a rotating palette based on feature index.
 */
function injectFeatureColors(geoJson: GeoJSON.GeoJSON): GeoJSON.GeoJSON {
  if (geoJson.type !== 'FeatureCollection') return geoJson;
  const fc = geoJson as GeoJSON.FeatureCollection;

  // Check if any feature already has a color — if so, leave them all alone
  const hasColors = fc.features.some(f => f.properties?.color);
  if (hasColors) return geoJson;

  // Inject colors from the deterministic palette
  return {
    ...fc,
    features: fc.features.map((f, i) => ({
      ...f,
      properties: {
        ...f.properties,
        color: DETERMINISTIC_COLORS[i % DETERMINISTIC_COLORS.length],
      },
    })),
  };
}

/**
 * Fire Modeling Dashboard Component
 *
 * Provides fire behavior modeling capabilities through Project Nomad.
 * Embeds the Nomad Dashboard with EM3's adapter for auth and map integration.
 */
const FireModelingDashboard: React.FC<FireModelingDashboardProps> = ({
  className = '',
  onClose,
}) => {
  const { userInfo, isLoggedIn } = useFusionAuth();
  const { registerLiveLayer, map } = useLayerDrawer();
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);

  // Store ignition geometry per model for "Add to Map" functionality
  const [modelIgnitions, setModelIgnitions] = useState<Map<string, GeoJSON.Geometry>>(new Map());
  const [showAbout, setShowAbout] = useState(false);

  // Ref to always have current map instance (avoids stale closures in Nomad action callbacks)
  const mapRef = useRef<mapboxgl.Map | null>(null);
  useEffect(() => { mapRef.current = map ?? null; }, [map]);

  // Bridge EM3's Mapbox map to Nomad's MapContext via a global ref.
  // Nomad's ArrivalAnimationManager calls useMap() during its FIRST render —
  // so the global must be set BEFORE children render, which means inside the
  // render body, not in a useEffect (effects fire after children mount). The
  // postinstall patch (scripts/patch-nomad-useMap.cjs) reads this global from
  // the strict useMap stub. Mapbox GL and MapLibre share the source/layer/
  // filter APIs the manager uses, so a Mapbox instance is acceptable.
  (globalThis as unknown as { __em3NomadMap?: mapboxgl.Map | null }).__em3NomadMap = map ?? null;
  useEffect(() => {
    return () => {
      const g = globalThis as unknown as { __em3NomadMap?: mapboxgl.Map | null };
      if (g.__em3NomadMap === map) g.__em3NomadMap = null;
    };
  }, [map]);

  // Popup ref for hover metadata display
  const hoverPopupRef = useRef<mapboxgl.Popup | null>(null);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Memoize the getMap function to avoid recreating adapter on every render
  const getMap = useCallback(() => map, [map]);

  // Get Nomad configuration from runtime config.
  //
  // #967: a missing value is reported, never substituted. This previously fell back
  // to a hardcoded absolute host when the resolved value was empty. Nomad is reached
  // through this application's own backend proxy and is never addressed by domain, so
  // that substitution turned "the application does not know where Nomad is" into a DNS
  // failure against a domain appearing nowhere else in the deployment — a condition
  // that should name itself, surfacing somewhere unrelated instead.
  const nomadConfig = useMemo<{ apiUrl: string; agencyId?: string; configError: string | null }>(() => {
    try {
      const config = getNomadConfig();
      if (!config.apiUrl) {
        return {
          apiUrl: '',
          configError: 'Fire Modelling is not configured: no Nomad API URL was resolved.',
        };
      }
      return { apiUrl: config.apiUrl, agencyId: config.agencyId, configError: null };
    } catch (e) {
      console.error('[FireModelingDashboard] Failed to get config:', e);
      const detail = e instanceof Error ? e.message : String(e);
      return {
        apiUrl: '',
        configError: `Fire Modelling configuration could not be loaded: ${detail}`,
      };
    }
  }, []);

  // Create the Nomad adapter
  const adapter = useMemo(() => {
    // #967: with no resolved URL there is nowhere to send a request, so build nothing.
    // Every adapter call interpolates this value directly (EM3NomadAdapter.ts:264), and
    // an empty base would issue requests against EM3's own origin instead.
    if (nomadConfig.configError) return null;

    console.log('[FireModelingDashboard] Creating adapter, nomadConfig:', {
      apiUrl: nomadConfig.apiUrl,
    });

    const newAdapter = createEM3NomadAdapter({
      nomadApiUrl: nomadConfig.apiUrl,
      agencyId: nomadConfig.agencyId,
      getUserInfo: () => {
        if (!userInfo) return null;
        return userInfo as FusionAuthUserInfo;
      },
      getAccessToken: () => localStorage.getItem('access_token'),
      getMap, // Pass map getter for spatial operations
      // Hydrate model.userId (UUID from Nomad) to "Firstname Lastname" for the
      // Dashboard's ModelCard. Falls back to username → email → raw UUID.
      //
      // Older Nomad models persisted a non-UUID string in userId
      // (e.g. "import", "Franco"). FusionAuth only accepts UUIDs, so calling
      // getUserById with those values produces a 500 from EM3's backend.
      // Skip the network call entirely when userId isn't UUID-shaped — the
      // raw string ("import" / "Franco" / etc.) is still meaningful, and the
      // adapter's hydrateUserId falls back to the raw value when the resolver
      // returns null.
      resolveUserName: async (userId: string) => {
        const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
        if (!UUID_RE.test(userId)) {
          // Not a UUID — leave the raw string in place. Common for older
          // models created before X-Nomad-User-Id forwarding sent a real id.
          return null;
        }
        try {
          const user = await getUserById(userId);
          if (!user) {
            console.warn('[FireModelingDashboard] FusionAuth returned no user for', userId);
            return null;
          }
          const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
          if (name) return name;
          if (user.username) return user.username;
          if (user.email) return user.email;
          console.warn('[FireModelingDashboard] FusionAuth user has no name/username/email', {
            userId,
            keys: Object.keys(user),
          });
          return null;
        } catch (err) {
          console.warn('[FireModelingDashboard] FusionAuth lookup failed for', userId, err);
          return null;
        }
      },
    });

    console.log('[FireModelingDashboard] Adapter created successfully');
    return newAdapter;
  }, [nomadConfig, userInfo, getMap]);

  /**
   * Read a File object as text string
   */
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  };

  /**
   * Handle wizard completion - create model and submit job
   * This is where we actually call the Nomad API
   */
  const handleWizardComplete = async (data: Record<string, unknown>) => {
    console.log('[FireModelingDashboard] Wizard complete, submitting model:', data);

    if (!adapter) {
      console.error('[FireModelingDashboard] No adapter available');
      return;
    }

    try {
      // Extract data from wizard (ModelSetupData shape)
      const geometry = data.geometry as { features?: Array<{ geometry: GeoJSON.Geometry }> };
      const temporal = data.temporal as { startDate: string; startTime: string; durationHours: number; timezone: string };
      const model = data.model as { engine: string; outputMode: string };
      const weather = data.weather as { source: string; firestarrCsvFile?: File; rawWeatherFile?: File; spotwxFile?: File; startingCodes?: { ffmc: number; dmc: number; dc: number } };
      const execution = data.execution as { notes?: string } | undefined;

      // Get the geometry from features
      const ignitionGeometry = geometry.features?.[0]?.geometry;
      if (!ignitionGeometry) {
        console.error('[FireModelingDashboard] No geometry in wizard data');
        return;
      }

      // Show ignition geometry on the map for visual feedback
      const ignitionSourceId = 'ignition-preview-source';
      const ignitionLayerId = 'ignition-preview-layer';
      const ignitionOutlineLayerId = 'ignition-preview-outline';
      if (map) {
        // Remove existing preview layers if any
        if (map.getLayer(ignitionOutlineLayerId)) map.removeLayer(ignitionOutlineLayerId);
        if (map.getLayer(ignitionLayerId)) map.removeLayer(ignitionLayerId);
        if (map.getSource(ignitionSourceId)) map.removeSource(ignitionSourceId);

        // Add ignition geometry as a source
        map.addSource(ignitionSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: ignitionGeometry,
            properties: {},
          } as GeoJSON.Feature,
        });

        // Add fill layer for polygon/point
        if (ignitionGeometry.type === 'Point') {
          map.addLayer({
            id: ignitionLayerId,
            type: 'circle',
            source: ignitionSourceId,
            paint: {
              'circle-radius': 8,
              'circle-color': '#e74c3c',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#c0392b',
            },
          });
        } else {
          map.addLayer({
            id: ignitionLayerId,
            type: 'fill',
            source: ignitionSourceId,
            paint: {
              'fill-color': '#e74c3c',
              'fill-opacity': 0.5,
            },
          });
          map.addLayer({
            id: ignitionOutlineLayerId,
            type: 'line',
            source: ignitionSourceId,
            paint: {
              'line-color': '#c0392b',
              'line-width': 3,
            },
          });
        }

        console.log('[FireModelingDashboard] Ignition geometry added to map preview');

        // Clear the mapbox-gl-draw features now that we have our own layer
        // This prevents duplicate geometry on the map
        try {
          const drawService = getDrawService(map);
          drawService.clearDrawnFeatures();
        } catch (e) {
          // Draw service may not be initialized if geometry was uploaded, not drawn
          console.debug('[FireModelingDashboard] Could not clear draw features:', e);
        }
      }

      // Read CSV file if provided
      let firestarrCsvContent: string | undefined;
      let rawWeatherContent: string | undefined;
      let spotwxCsvData: string | undefined;
      if (weather.source === 'firestarr_csv' && weather.firestarrCsvFile) {
        firestarrCsvContent = await readFileAsText(weather.firestarrCsvFile);
        console.log('[FireModelingDashboard] Read firestarr CSV file, length:', firestarrCsvContent.length);
      } else if (weather.source === 'raw_weather' && weather.rawWeatherFile) {
        rawWeatherContent = await readFileAsText(weather.rawWeatherFile);
        console.log('[FireModelingDashboard] Read raw weather file, length:', rawWeatherContent.length);
      } else if (weather.source === 'spotwx' && weather.spotwxFile) {
        // #694: An UPLOADED SpotWX forecast — carry the raw CSV text on
        // spotwxCsvData so the adapter normalizes it to raw_weather via
        // openNomad's resolveWeatherParams (no live fetch / no SPOTWX_API_KEY).
        spotwxCsvData = await readFileAsText(weather.spotwxFile);
        console.log('[FireModelingDashboard] Read uploaded SpotWX file, length:', spotwxCsvData.length);
      }

      // Build model create params
      // Note: We pass both 'engine' (for TypeScript type) and 'engineType' (for backend API)
      // The adapter handles the mapping
      const engineValue = model.engine as 'firestarr' | 'wise';
      const modelParams = {
        name: `Fire Model ${new Date().toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '')}`,
        engine: engineValue,      // For TypeScript ModelCreateParams type
        engineType: engineValue,  // For Nomad backend API
        geometry: ignitionGeometry,
        temporal: {
          startDate: temporal.startDate,
          startTime: temporal.startTime,
          durationHours: temporal.durationHours,
          timezone: temporal.timezone,
        },
        weather: {
          source: weather.source === 'firestarr_csv' ? 'csv' as const :
                  weather.source === 'raw_weather' ? 'csv' as const :
                  'spotwx' as const,
          firestarrCsvContent,
          rawWeatherContent,
          spotwxCsvData,
          startingCodes: weather.startingCodes,
        },
        output: {
          mode: model.outputMode as 'probabilistic' | 'deterministic',
        },
        notes: execution?.notes,
      };

      console.log('[FireModelingDashboard] Creating model with params:', modelParams);

      // models.create / jobs.submit are OPTIONAL capabilities in the Nomad
      // contract (0.11+). EM3's ACN adapter always provides them; fail fast if a
      // future adapter does not rather than silently no-op.
      const createModel = adapter.models.create;
      const submitJob = adapter.jobs.submit;
      if (!createModel || !submitJob) {
        throw new Error('Nomad adapter does not support model create/submit');
      }

      // Create the model
      // Type assertion needed because our adapter accepts extended params beyond ModelCreateParams
      // (geometry, temporal, output fields for execution - see EM3NomadAdapter.ts)
      const createdModel = await createModel(modelParams as Parameters<typeof createModel>[0]);
      console.log('[FireModelingDashboard] Model created:', createdModel);

      // Store ignition geometry for later "Add to Map" functionality
      setModelIgnitions(prev => new Map(prev).set(createdModel.id, ignitionGeometry));

      // Submit the job
      const job = await submitJob(createdModel.id);
      console.log('[FireModelingDashboard] Job submitted:', job);

      // Show success notification
      setNotification({
        type: 'success',
        message: `Model submitted! Job ${job.jobId.slice(0, 8)}... is running. Check Active Jobs for progress.`,
      });

    } catch (err) {
      console.error('[FireModelingDashboard] Failed to create/submit model:', err);
      setNotification({
        type: 'error',
        message: `Failed to submit model: ${err instanceof Error ? err.message : 'Unknown error'}`,
      });
    }
  };

  /**
   * Handle adding GeoJSON (perimeter) to map from internal results view
   */
  const handleAddGeoJsonToMap = useCallback((
    output: { id: string; type: string; name: string },
    geoJson: GeoJSON.GeoJSON,
    modelInfo?: { modelId: string; modelName: string; engineType: string }
  ) => {
    const currentMap = mapRef.current;
    if (!currentMap) return;

    // Inject colors on features that lack them (deterministic outputs)
    const coloredGeoJson = injectFeatureColors(geoJson);

    const outputId = output.id;
    const modelName = modelInfo?.modelName || output.name || 'Unknown Model';
    const sourceId = `nomad-geojson-source-${outputId}`;
    const fillLayerId = `nomad-geojson-fill-${outputId}`;
    const outlineLayerId = `nomad-geojson-outline-${outputId}`;

    // Diagnostic: capture the actual shape we received so when "nothing
    // appears on the map" happens we can tell whether the geoJson is empty,
    // mis-projected, or just rendered off-viewport. Bounded log — no
    // attempt to log the whole feature collection.
    const featureCount = (geoJson as GeoJSON.FeatureCollection)?.features?.length ?? 0;
    const firstFeatureType = (geoJson as GeoJSON.FeatureCollection)?.features?.[0]?.geometry?.type ?? null;
    const firstCoord = (() => {
      const fc = geoJson as GeoJSON.FeatureCollection;
      const g = fc?.features?.[0]?.geometry as GeoJSON.Geometry | undefined;
      if (!g) return null;
      if (g.type === 'Point') return g.coordinates;
      if (g.type === 'LineString' || g.type === 'MultiPoint') return g.coordinates?.[0];
      if (g.type === 'Polygon' || g.type === 'MultiLineString') return g.coordinates?.[0]?.[0];
      if (g.type === 'MultiPolygon') return g.coordinates?.[0]?.[0]?.[0];
      return null;
    })();
    console.log('[FireModelingDashboard] Adding GeoJSON to map', {
      outputId,
      modelName,
      featureCount,
      firstFeatureType,
      firstCoord,
      outputType: output.type,
    });

    // Add or update source
    const existingSource = currentMap.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (existingSource) {
      existingSource.setData(coloredGeoJson as GeoJSON.FeatureCollection);
    } else {
      currentMap.addSource(sourceId, {
        type: 'geojson',
        data: coloredGeoJson as GeoJSON.FeatureCollection,
      });
    }

    // Add fill layer with data-driven styling (reads color from feature properties)
    if (!currentMap.getLayer(fillLayerId)) {
      currentMap.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': ['coalesce', ['get', 'color'], '#ff6b35'],
          'fill-opacity': 0.5,
        },
      });
    }

    // Add outline layer
    if (!currentMap.getLayer(outlineLayerId)) {
      currentMap.addLayer({
        id: outlineLayerId,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': ['coalesce', ['get', 'color'], '#d63031'],
          'line-width': 2,
        },
      });
    }

    // Add hover handlers
    currentMap.on('mouseenter', fillLayerId, (e) => {
      currentMap.getCanvas().style.cursor = 'pointer';
      if (e.features && e.features.length > 0) {
        const props = e.features[0].properties || {};
        let html = `<div class="fire-model-popup">`;
        html += `<div class="fire-model-popup__title">${modelName}</div>`;
        if (props.percentile) html += `<div class="fire-model-popup__row"><span>Percentile:</span> ${props.percentile}%</div>`;
        if (props.area_ha) html += `<div class="fire-model-popup__row"><span>Area:</span> ${Number(props.area_ha).toFixed(0)} ha</div>`;
        html += `</div>`;

        if (!hoverPopupRef.current) {
          hoverPopupRef.current = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, className: 'fire-model-hover-popup' });
        }
        hoverPopupRef.current.setLngLat(e.lngLat).setHTML(html).addTo(currentMap);
      }
    });
    currentMap.on('mousemove', fillLayerId, (e) => { if (hoverPopupRef.current && e.lngLat) hoverPopupRef.current.setLngLat(e.lngLat); });
    currentMap.on('mouseleave', fillLayerId, () => { currentMap.getCanvas().style.cursor = ''; hoverPopupRef.current?.remove(); });

    // Register as live layer
    registerLiveLayer(
      {
        id: `nomad-geojson-${outputId}`,
        name: `${output.name || 'Perimeter'}: ${modelName}`,
        shortName: output.name || 'Perimeter',
        icon: 'fa-solid fa-fire',
        description: `FireSTARR perimeter`,
        enabled: true,
      },
      (enabled) => {
        const visibility = enabled ? 'visible' : 'none';
        const m = mapRef.current;
        if (m?.getLayer(fillLayerId)) m.setLayoutProperty(fillLayerId, 'visibility', visibility);
        if (m?.getLayer(outlineLayerId)) m.setLayoutProperty(outlineLayerId, 'visibility', visibility);
      }
    );

    setNotification({ type: 'success', message: `Added ${modelName} perimeter to map` });
  }, [registerLiveLayer]);

  /**
   * Add a raster layer to the map (shared helper for probability outputs)
   *
   * @param resultId - The result ID for the raster
   * @param modelName - Display name for the model
   * @param bounds - Optional bounding box for the raster tiles
   * @param beforeLayerId - Optional layer ID to insert the raster beneath
   * @param tileUrlOverride - If provided, used verbatim. Required for arrival_time
   *   outputs which aren't DB result records and don't have a /results/:id
   *   tile endpoint — Nomad supplies a model-scoped tileUrlTemplate instead.
   */
  const addRasterLayerToMap = useCallback((
    resultId: string,
    modelName: string,
    bounds?: [number, number, number, number],
    beforeLayerId?: string,
    tileUrlOverride?: string
  ) => {
    if (!map || !adapter) return;

    const tileUrl = tileUrlOverride ?? adapter.results.getTileUrlTemplate(resultId);
    const rasterSourceId = `nomad-raster-source-${resultId}`;
    const rasterLayerId = `nomad-raster-layer-${resultId}`;

    // Remove existing raster layer/source if re-adding
    if (map.getLayer(rasterLayerId)) map.removeLayer(rasterLayerId);
    if (map.getSource(rasterSourceId)) map.removeSource(rasterSourceId);

    // Add raster source
    const sourceSpec: mapboxgl.RasterSourceSpecification = {
      type: 'raster',
      tiles: [tileUrl],
      tileSize: 256,
    };
    if (bounds) sourceSpec.bounds = bounds;
    map.addSource(rasterSourceId, sourceSpec);

    // Add raster layer
    map.addLayer(
      {
        id: rasterLayerId,
        type: 'raster',
        source: rasterSourceId,
        paint: {
          'raster-opacity': 0.7,
        },
      },
      beforeLayerId
    );

    // Register with LayerDrawer for toggle control
    registerLiveLayer(
      {
        id: `nomad-probability-${resultId}`,
        name: `Probability: ${modelName}`,
        shortName: 'Probability',
        icon: 'fa-solid fa-layer-group',
        description: 'Burn probability heatmap',
        enabled: true,
      },
      (enabled) => {
        const visibility = enabled ? 'visible' : 'none';
        if (map.getLayer(rasterLayerId)) {
          map.setLayoutProperty(rasterLayerId, 'visibility', visibility);
        }
      }
    );
  }, [map, adapter, registerLiveLayer]);

  /**
   * Handle adding raster (probability or arrival_time) to map from internal
   * results view.
   *
   * Nomad provides a `tileUrl` template that's correct for the output type:
   *   - probability: /api/v1/results/:resultId/tile/{z}/{x}/{y}.png
   *   - arrival_time: /api/v1/models/:modelId/arrival-tile/{z}/{x}/{y}.png
   *
   * Older EM3 ignored that and reconstructed via adapter.results, which only
   * worked for probability (arrival_time has no DB result record so the
   * reconstructed URL 404s silently). Use Nomad's tileUrl directly when
   * present.
   */
  const handleAddRasterToMap = useCallback((
    output: { id: string; type: string; name: string },
    bounds: [number, number, number, number],
    tileUrl: string,
    modelInfo?: { modelId: string; modelName: string; engineType: string }
  ) => {
    const resultId = output.id;
    const modelName = modelInfo?.modelName || output.name || 'Unknown Model';

    addRasterLayerToMap(resultId, modelName, bounds, undefined, tileUrl);
    const labelByType: Record<string, string> = {
      arrival_time: 'arrival time',
      probability: 'probability',
    };
    const label = labelByType[output.type] || (output.name || 'raster');
    setNotification({ type: 'success', message: `Added ${modelName} ${label} to map` });
  }, [addRasterLayerToMap]);

  /**
   * Handle "Add to Map" callback from Dashboard (model list view)
   * Adds model results as a layer on the map
   */
  const handleAddToMap = async (modelId: string) => {
    console.log('[FireModelingDashboard] handleAddToMap (model list button) CALLED', { modelId });

    if (!adapter) return;

    try {
      // Fetch model results
      const modelResults = await adapter.results.get(modelId);

      // Backend returns 'outputs' array with downloadUrl, not 'results' with separate getData endpoint
      const backendResults = modelResults as unknown as {
        modelName: string;
        outputs?: Array<{ type: string; id: string; downloadUrl: string; name: string; previewUrl?: string }>;
        executionSummary?: { totalAreaHa?: number };
      };

      // Find a displayable output - prefer probability (colored zones) over perimeter (single polygon)
      const outputs = backendResults.outputs;

      // Prefer probability (colored zones via preview endpoint) over perimeter (single polygon)
      const displayResult = outputs?.find(r => r.type === 'probability')
        || outputs?.find(r => r.type === 'perimeter')
        || outputs?.[0];

      if (!displayResult) {
        console.warn('[FireModelingDashboard] No displayable result found for model', modelId);
        return;
      }

      // Determine which URL to fetch:
      // - probability outputs: use previewUrl (backend converts raster to colored GeoJSON contours)
      // - perimeter outputs: use downloadUrl (already GeoJSON)
      const fetchUrl = displayResult.type === 'probability'
        ? displayResult.previewUrl
        : displayResult.downloadUrl;

      if (!fetchUrl) {
        console.error('[FireModelingDashboard] No URL available for result', displayResult.id, displayResult.type);
        return;
      }

      // results.getData is an OPTIONAL capability (Nomad 0.11+); EM3's adapter
      // provides it. Bail clearly if a future adapter does not.
      const getResultData = adapter.results.getData;
      if (!getResultData) {
        console.error('[FireModelingDashboard] adapter.results.getData not supported');
        return;
      }
      const geoJsonData = await getResultData(fetchUrl);

      if (typeof geoJsonData === 'string') {
        // getData returned a URL instead of data (shouldn't happen for preview/perimeter)
        console.warn('[FireModelingDashboard] Unexpected URL response, cannot display');
        return;
      }

      // Add GeoJSON to map — use output ID for unique layer identification
      const modelName = backendResults.modelName || 'Unknown Model';
      const totalAreaHa = backendResults.executionSummary?.totalAreaHa;
      const outputId = displayResult.id;
      const sourceId = `nomad-geojson-source-${outputId}`;
      const fillLayerId = `nomad-geojson-fill-${outputId}`;
      const outlineLayerId = `nomad-geojson-outline-${outputId}`;

      if (map) {
        // Add or update source
        const existingSrc = map.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
        if (existingSrc) {
          existingSrc.setData(geoJsonData as unknown as GeoJSON.FeatureCollection);
        } else {
          map.addSource(sourceId, {
            type: 'geojson',
            data: geoJsonData as unknown as GeoJSON.FeatureCollection,
          });
        }

        // Add fill layer with data-driven styling (reads color from feature properties)
        if (!map.getLayer(fillLayerId)) {
          map.addLayer({
            id: fillLayerId,
            type: 'fill',
            source: sourceId,
            paint: {
              'fill-color': ['coalesce', ['get', 'color'], '#ff6b35'],
              'fill-opacity': 0.5,
            },
          });
        }

        // Add outline layer
        if (!map.getLayer(outlineLayerId)) {
          map.addLayer({
            id: outlineLayerId,
            type: 'line',
            source: sourceId,
            paint: {
              'line-color': ['coalesce', ['get', 'color'], '#d63031'],
              'line-width': 2,
            },
          });
        }

        // Add hover handlers for metadata popup
        map.on('mouseenter', fillLayerId, (e) => {
          map.getCanvas().style.cursor = 'pointer';

          if (e.features && e.features.length > 0) {
            const feature = e.features[0];
            const props = feature.properties || {};

            // Build popup content
            let html = `<div class="fire-model-popup">`;
            html += `<div class="fire-model-popup__title">${modelName}</div>`;
            if (totalAreaHa) {
              html += `<div class="fire-model-popup__row"><span>Total Area:</span> ${totalAreaHa.toFixed(0)} ha</div>`;
            }
            // Show feature properties if available (e.g., probability percentile)
            if (props.percentile) {
              html += `<div class="fire-model-popup__row"><span>Percentile:</span> ${props.percentile}%</div>`;
            }
            if (props.probability) {
              html += `<div class="fire-model-popup__row"><span>Probability:</span> ${(props.probability * 100).toFixed(1)}%</div>`;
            }
            if (props.area_ha) {
              html += `<div class="fire-model-popup__row"><span>Feature Area:</span> ${Number(props.area_ha).toFixed(0)} ha</div>`;
            }
            html += `</div>`;

            // Create or reuse popup
            if (!hoverPopupRef.current) {
              hoverPopupRef.current = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false,
                className: 'fire-model-hover-popup',
              });
            }

            hoverPopupRef.current
              .setLngLat(e.lngLat)
              .setHTML(html)
              .addTo(map);
          }
        });

        map.on('mousemove', fillLayerId, (e) => {
          // Update popup position as mouse moves
          if (hoverPopupRef.current && e.lngLat) {
            hoverPopupRef.current.setLngLat(e.lngLat);
          }
        });

        map.on('mouseleave', fillLayerId, () => {
          map.getCanvas().style.cursor = '';
          if (hoverPopupRef.current) {
            hoverPopupRef.current.remove();
          }
        });
      }

      // Register perimeter as a live layer for UI toggle
      registerLiveLayer(
        {
          id: `nomad-geojson-${outputId}`,
          name: `${displayResult.name || 'Result'}: ${modelName}`,
          shortName: displayResult.name || 'Result',
          icon: 'fa-solid fa-fire',
          description: `FireSTARR perimeter - ${totalAreaHa?.toFixed(0) || 'N/A'} ha`,
          enabled: true,
        },
        (enabled) => {
          if (map) {
            const visibility = enabled ? 'visible' : 'none';
            if (map.getLayer(fillLayerId)) {
              map.setLayoutProperty(fillLayerId, 'visibility', visibility);
            }
            if (map.getLayer(outlineLayerId)) {
              map.setLayoutProperty(outlineLayerId, 'visibility', visibility);
            }
          }
        }
      );

      // Add probability raster layer(s) if available (below perimeter layers so vector is on top)
      const probabilityOutputs = outputs?.filter(o => o.type === 'probability') || [];
      const beforeLayerId = map?.getLayer(fillLayerId) ? fillLayerId : undefined;
      for (const probOutput of probabilityOutputs) {
        if (!probOutput.id) continue;
        addRasterLayerToMap(probOutput.id, probOutput.name || modelName, undefined, beforeLayerId);
      }

      // Add ignition layer if we have the geometry stored
      const ignitionGeometry = modelIgnitions.get(modelId);
      if (ignitionGeometry && map) {
        const ignitionSourceId = `nomad-ignition-source-${modelId}`;
        const ignitionLayerId = `nomad-ignition-layer-${modelId}`;
        const ignitionOutlineId = `nomad-ignition-outline-${modelId}`;

        // Remove existing if any
        if (map.getLayer(ignitionOutlineId)) map.removeLayer(ignitionOutlineId);
        if (map.getLayer(ignitionLayerId)) map.removeLayer(ignitionLayerId);
        if (map.getSource(ignitionSourceId)) map.removeSource(ignitionSourceId);

        // Add ignition source
        map.addSource(ignitionSourceId, {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: ignitionGeometry,
            properties: {},
          } as GeoJSON.Feature,
        });

        // Add ignition layers (red/darker to distinguish from perimeter)
        if (ignitionGeometry.type === 'Point') {
          map.addLayer({
            id: ignitionLayerId,
            type: 'circle',
            source: ignitionSourceId,
            paint: {
              'circle-radius': 8,
              'circle-color': '#9b59b6',
              'circle-stroke-width': 2,
              'circle-stroke-color': '#8e44ad',
            },
          });
        } else {
          map.addLayer({
            id: ignitionLayerId,
            type: 'fill',
            source: ignitionSourceId,
            paint: {
              'fill-color': '#9b59b6',
              'fill-opacity': 0.5,
            },
          });
          map.addLayer({
            id: ignitionOutlineId,
            type: 'line',
            source: ignitionSourceId,
            paint: {
              'line-color': '#8e44ad',
              'line-width': 3,
            },
          });
        }

        // Register ignition as separate live layer
        registerLiveLayer(
          {
            id: `nomad-ignition-${modelId}`,
            name: `Ignition: ${modelName}`,
            shortName: 'Ignition',
            icon: 'fa-solid fa-location-dot',
            description: 'Model ignition area',
            enabled: true,
          },
          (enabled) => {
            if (map) {
              const visibility = enabled ? 'visible' : 'none';
              if (map.getLayer(ignitionLayerId)) {
                map.setLayoutProperty(ignitionLayerId, 'visibility', visibility);
              }
              if (map.getLayer(ignitionOutlineId)) {
                map.setLayoutProperty(ignitionOutlineId, 'visibility', visibility);
              }
            }
          }
        );
      }

      console.log('[FireModelingDashboard] Model added to map', {
        modelId,
        name: modelName,
        area: totalAreaHa,
        featureCount: (geoJsonData as unknown as GeoJSON.FeatureCollection)?.features?.length,
        hasIgnition: !!ignitionGeometry,
      });

    } catch (e) {
      console.error('[FireModelingDashboard] Failed to add model to map:', e);
    }
  };

  /**
   * Clear all fire modeling layers from the map
   * Removes model perimeters, ignition geometry, and draw preview layers
   */
  const handleClearMap = useCallback(() => {
    const currentMap = mapRef.current;
    if (!currentMap) return;

    // Clear DrawService features (preview layers from drawing)
    try {
      const drawService = getDrawService(currentMap);
      drawService.clearDrawnFeatures();
    } catch (e) {
      console.debug('[FireModelingDashboard] Could not clear draw features:', e);
    }

    // Remove all nomad-* and ignition-preview-* layers and sources
    const style = currentMap.getStyle();
    if (style?.layers) {
      const layersToRemove = style.layers
        .filter(layer =>
          layer.id.startsWith('nomad-model-') ||
          layer.id.startsWith('nomad-geojson-') ||
          layer.id.startsWith('nomad-ignition-') ||
          layer.id.startsWith('nomad-raster-') ||
          layer.id.startsWith('ignition-preview-')
        )
        .map(layer => layer.id);

      layersToRemove.forEach(layerId => {
        if (currentMap.getLayer(layerId)) {
          currentMap.removeLayer(layerId);
        }
      });
    }

    // Remove sources
    if (style?.sources) {
      Object.keys(style.sources).forEach(sourceId => {
        if (
          sourceId.startsWith('nomad-model-source-') ||
          sourceId.startsWith('nomad-geojson-source-') ||
          sourceId.startsWith('nomad-ignition-source-') ||
          sourceId.startsWith('nomad-raster-source-') ||
          sourceId.startsWith('ignition-preview-')
        ) {
          if (currentMap.getSource(sourceId)) {
            currentMap.removeSource(sourceId);
          }
        }
      });
    }

    // Clear stored ignition geometry
    setModelIgnitions(new Map());

    console.log('[FireModelingDashboard] Map cleared');
    setNotification({
      type: 'success',
      message: 'Map cleared',
    });
  }, []);

  // Show error state. A configuration that could not be resolved is reported here
  // rather than further down as "Initializing fire modeling...", which would describe
  // a wait that is never going to end (#967).
  if (error || nomadConfig.configError) {
    return (
      <div className={`fire-modeling-dashboard fire-modeling-dashboard--error ${className}`}>
        <div className="error-message">
          <h3>Configuration Error</h3>
          <p>{error ?? nomadConfig.configError}</p>
        </div>
      </div>
    );
  }

  // Show login required state
  if (!isLoggedIn) {
    return (
      <div className={`fire-modeling-dashboard fire-modeling-dashboard--auth-required ${className}`}>
        <div className="auth-message">
          <h3>Authentication Required</h3>
          <p>Please log in to access fire modeling features.</p>
        </div>
      </div>
    );
  }

  // Show loading state while adapter is being created
  if (!adapter) {
    return (
      <div className={`fire-modeling-dashboard fire-modeling-dashboard--loading ${className}`}>
        <div className="loading-message">
          <p>Initializing fire modeling...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fire-modeling-dashboard ${className}`}>
      {/* Hidden CSH trigger — activated by Nomad toolbar button */}
      <div id="fire-modeling-csh-trigger" style={{ position: 'absolute', top: -9999, left: -9999 }}>
        <ContextHelp title='Help with Fire Modeling' context='fire-modeling-help' />
      </div>

      {/* Notification toast */}
      {notification && (
        <div
          className={`fire-modeling-notification fire-modeling-notification--${notification.type}`}
          onClick={() => setNotification(null)}
        >
          {notification.message}
        </div>
      )}
      <OpenNomadProvider adapter={adapter}>
        <DashboardContainer
          mode="floating"
          topGutter={60}
          title={`NWT Fire Modelling [OpenNomad v${nomadVersion}]`}
          actions={[
            { id: 'clear-map', label: 'Clear Map', placement: 'header', onClick: handleClearMap },
            { id: 'about', label: '', icon: <img src="/logos/nomad-logo.png" alt="Project Nomad" style={{ height: 50, borderRadius: 4, display: 'block' }} />, placement: 'header', onClick: () => setShowAbout(true), tooltip: 'About OpenNomad', style: { padding: 0, gap: 0 } },
            { id: 'help', label: '?', placement: 'header', onClick: () => {
              const el = document.querySelector('#fire-modeling-csh-trigger .context-help-icon');
              if (el) (el as HTMLElement).click();
            }}
          ]}
          onClose={onClose}
          onWizardComplete={handleWizardComplete}
          onAddToMap={handleAddToMap}
          onAddGeoJsonToMap={handleAddGeoJsonToMap}
          onAddRasterToMap={handleAddRasterToMap}
        />
      </OpenNomadProvider>
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}
    </div>
  );
};

export default FireModelingDashboard;
